import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  RotateCcw,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { useAuthStore } from "@/store/useAuthStore";
import { useTaskList, useTasksByClient } from "@/hooks/useTasks";
import { useMyRequests } from "@/hooks/useInternalRequests";
import { BUCKETS, SCOPE_LABELS } from "@/components/task/constants";
import { TaskRow } from "@/components/task/TaskRow";
import { TaskBucketGroup } from "@/components/task/TaskBucketGroup";
import { TaskDetailDrawer } from "@/components/task/TaskDetailDrawer";
import { TaskCompleteModal, type CompleteTarget } from "@/components/task/TaskCompleteModal";
import { CreateMenu } from "@/components/home/CreateMenu";
import { OUTCOME_LABEL, type TaskOutcome } from "@/api/client360Tasks";
import { REQUEST_STATUS_LABEL } from "@/api/internalRequests";
import type {
  TaskBucket,
  TaskListItem,
  TaskScope,
  TaskSubtypeApi,
} from "@/api/types";

/** v1.2 §6 — the 5 canonical views (VISUAL_Task_Management_v1_2_LOCK). */
type TabKey = "todo" | "iset" | "fu" | "byclient" | "team";

/** «Мені треба зробити» filter chips (client-side over backend-scoped items). */
type TodoChip = "all" | "overdue" | "call" | "follow_up" | "bi_signal";
const TODO_CHIPS: Array<{ k: TodoChip; label: string }> = [
  { k: "all", label: "Усі" },
  { k: "overdue", label: "Прострочені" },
  { k: "call", label: "Дзвінки" },
  { k: "follow_up", label: "Follow-up" },
  { k: "bi_signal", label: "BI-сигнали" },
];

type TeamChip = "all" | "overdue" | "no_outcome";

export function TasksListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedActionsMe = useAuthStore((s) => s.permissions?.allowedActions);
  // task=true is the safe universal default (everyone creates own tasks);
  // request/meeting stay hidden until the backend explicitly allows them (§20).
  const allowedActions = allowedActionsMe ?? { task: true, internal_request: false, meeting: false };
  // FS-3: per-row edit-gating (Task ACL edit:own; admin/super-admin = edit:all). UI-only.
  const currentUserId = useAuthStore((s) => s.user?.id);
  const userType = useAuthStore((s) => s.user?.type);
  const canEditAnyTask = userType === "admin" || userType === "super-admin";

  const [tab, setTab] = useState<TabKey>("todo");
  const [scope, setScope] = useState<TaskScope>("my");
  const [todoChip, setTodoChip] = useState<TodoChip>("all");
  const [teamChip, setTeamChip] = useState<TeamChip>("all");
  const [byManager, setByManager] = useState<string>("");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [completeTarget, setCompleteTarget] = useState<CompleteTarget | null>(null);
  const [doneOpen, setDoneOpen] = useState(false);
  const [expandedHoldings, setExpandedHoldings] = useState<Record<string, boolean>>({});

  // ── queries per tab (only the active one fetches) ──
  const todoQ = useTaskList({ scope, view: "all" }, tab === "todo");
  const isetQ = useTaskList({ view: "i_assigned" }, tab === "iset");
  const myReqQ = useMyRequests(tab === "iset");
  const fuQ = useTaskList({ view: "follow_up" }, tab === "fu");
  const byClientQ = useTasksByClient(scope, tab === "byclient");
  const teamQ = useTaskList(
    {
      scope: "team",
      view: "all",
      teamFilter: teamChip === "overdue" ? "overdue" : teamChip === "no_outcome" ? "completed_no_outcome" : undefined,
      byManager: byManager || undefined,
    },
    tab === "team",
  );

  const allowedScopes: TaskScope[] = todoQ.data?.allowedScopes
    ?? fuQ.data?.allowedScopes
    ?? byClientQ.data?.allowedScopes
    ?? ["my"];
  const teamAllowed = allowedScopes.includes("team");

  // ── deep-links: ?open=id · ?bucket=overdue · ?filter=mine|overdue|followup
  //    · ?scope=team (Supervisor Home C2 → team tab; backend tier-revalidates,
  //      manager→own clamp so the team tab simply stays hidden) ──
  useEffect(() => {
    const open = searchParams.get("open");
    const bucket = searchParams.get("bucket");
    const filter = searchParams.get("filter");
    const scope = searchParams.get("scope");
    if (!open && !bucket && !filter && !scope) return;
    if (open) setSelectedTaskId(open);
    if (scope === "team") {
      setTab("team");
      if (bucket === "overdue" || filter === "overdue") setTeamChip("overdue");
    } else if (bucket === "overdue" || filter === "overdue") {
      setTab("todo");
      setTodoChip("overdue");
    } else if (filter === "mine") {
      setTab("todo");
    } else if (filter === "followup") {
      setTab("fu");
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openComplete = (t: TaskListItem) =>
    setCompleteTarget({ id: t.id, title: t.title, subtype: t.subtype, holdingId: t.holdingId });

  // ── «Мені треба зробити» client-side chips + bucket grouping ──
  const todoItems = useMemo(() => {
    const items = todoQ.data?.items ?? [];
    if (todoChip === "overdue") return items.filter((t) => t.bucket === "overdue");
    if (todoChip === "call" || todoChip === "follow_up" || todoChip === "bi_signal") {
      return items.filter((t) => t.subtype === (todoChip as TaskSubtypeApi));
    }
    return items;
  }, [todoQ.data, todoChip]);

  const todoGrouped = useMemo(() => {
    const map: Record<TaskBucket, TaskListItem[]> = { overdue: [], today: [], week: [], later: [] };
    for (const item of todoItems) map[item.bucket].push(item);
    return map;
  }, [todoItems]);

  // team by-manager options derived from backend-scoped team items.
  const teamManagers = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teamQ.data?.items ?? []) {
      if (t.assigneeId && t.assigneeName) m.set(t.assigneeId, t.assigneeName);
    }
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [teamQ.data]);

  const tabs: Array<{ k: TabKey; label: string; count?: number }> = [
    { k: "todo", label: "Мені треба зробити", count: tab === "todo" ? todoQ.data?.total : undefined },
    { k: "iset", label: "Я поставив" },
    { k: "fu", label: "Follow-up", count: tab === "fu" ? fuQ.data?.total : undefined },
    { k: "byclient", label: "По клієнтах" },
    ...(teamAllowed ? [{ k: "team" as TabKey, label: "Команда" }] : []),
  ];

  return (
    <>
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[1040px] space-y-5">
          {/* Header */}
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Завдання</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Task = 1 сутність + підтип · результат обов'язковий при закритті · grain = holding
              </p>
            </div>
            {/* «+ Створити» — backend allowedActions-gated canonical menu (D-031/§20) */}
            <CreateMenu allowedActions={allowedActions} onDone={() => void todoQ.refetch()} />
          </div>

          {/* Scope pill (resolver allowedScopes; team tab is fixed team-scope) */}
          {allowedScopes.length > 1 && tab !== "team" && tab !== "iset" && tab !== "fu" && (
            <div className="inline-flex rounded-lg border bg-card p-1">
              {allowedScopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    scope === s
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {SCOPE_LABELS[s]}
                </button>
              ))}
            </div>
          )}

          {/* Tabs — 5 views (VISUAL) */}
          <div className="flex flex-wrap items-center gap-2">
            {tabs.map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                className={cn(
                  "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
                  tab === t.k
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
                {typeof t.count === "number" && <span className="ml-1.5 opacity-80">{t.count}</span>}
              </button>
            ))}
          </div>

          {/* ════ TAB 1: Мені треба зробити ════ */}
          {tab === "todo" && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {TODO_CHIPS.map((c) => (
                  <button
                    key={c.k}
                    type="button"
                    onClick={() => setTodoChip(c.k)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      todoChip === c.k
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {c.label}
                    {c.k === "overdue" && (todoQ.data?.counters.overdue ?? 0) > 0 && (
                      <span className="ml-1 text-destructive">· {todoQ.data!.counters.overdue}</span>
                    )}
                  </button>
                ))}
              </div>

              {todoQ.isPending ? (
                <ListSkeleton />
              ) : todoQ.isError ? (
                <ListError message={(todoQ.error as Error)?.message} onRetry={() => void todoQ.refetch()} />
              ) : todoItems.length === 0 && (todoQ.data?.recentCompleted.length ?? 0) === 0 ? (
                <EmptyState createSlot={<CreateMenu allowedActions={allowedActions} onDone={() => void todoQ.refetch()} />} />
              ) : (
                <div className="space-y-6">
                  {BUCKETS.map((b) => {
                    const items = todoGrouped[b.k];
                    if (items.length === 0) return null;
                    return (
                      <TaskBucketGroup key={b.k} def={b} count={items.length}>
                        {items.map((task) => (
                          <TaskRow
                            key={task.id}
                            task={task}
                            showAssignee={scope !== "my"}
                            onOpen={() => setSelectedTaskId(task.id)}
                            onComplete={() => openComplete(task)}
                            // D-142.W1: watched-рядки не-own у my-scope → без
                            // dead-CTA «Завершити» (403-глухий кут, §0)
                            isOwn={canEditAnyTask || (!!currentUserId && task.assigneeId === currentUserId)}
                          />
                        ))}
                      </TaskBucketGroup>
                    );
                  })}

                  {/* Done section (collapsible, read-only) */}
                  {(todoQ.data?.recentCompleted.length ?? 0) > 0 && (
                    <section className="pt-2 border-t">
                      <button
                        type="button"
                        onClick={() => setDoneOpen((v) => !v)}
                        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        {doneOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        Виконано нещодавно ({todoQ.data!.recentCompleted.length})
                      </button>
                      {doneOpen && (
                        <ul className="mt-2 space-y-1.5">
                          {todoQ.data!.recentCompleted.map((c) => (
                            <li key={c.id} className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                              <DoneDot />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-muted-foreground line-through">{c.title}</div>
                                <div className="truncate text-xs text-muted-foreground">
                                  {c.accountName ?? "Загальна"}
                                  {c.outcome && <> · {OUTCOME_LABEL[c.outcome as TaskOutcome] ?? c.outcome}</>}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setSelectedTaskId(c.id)}
                                className="text-xs text-muted-foreground hover:text-foreground"
                              >
                                Деталі →
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}
                </div>
              )}
            </>
          )}

          {/* ════ TAB 2: Я поставив (DUAL — НЕ зливати, §6.2/AC-T18) ════ */}
          {tab === "iset" && (
            <div className="space-y-5">
              <section className="overflow-hidden rounded-xl border bg-card">
                <header className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold">
                  Задачі, які я поставив
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">Task</span>
                </header>
                {isetQ.isPending ? (
                  <ListSkeleton inset />
                ) : isetQ.isError ? (
                  <ListError message={(isetQ.error as Error)?.message} onRetry={() => void isetQ.refetch()} inset />
                ) : (isetQ.data?.items.length ?? 0) === 0 && (isetQ.data?.recentCompleted.length ?? 0) === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                    Ви ще не ставили задач. «+ Створити → Задача» — виконавця обирає керівник за scope.
                  </p>
                ) : (
                  <div className="space-y-1.5 p-3">
                    {(isetQ.data?.items ?? []).map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        showAssignee
                        onOpen={() => setSelectedTaskId(task.id)}
                        onComplete={() => openComplete(task)}
                      />
                    ))}
                    {(isetQ.data?.recentCompleted ?? []).map((c) => (
                      <div key={c.id} className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                        <DoneDot />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-muted-foreground line-through">{c.title}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {c.assigneeName ? `Виконавець: ${c.assigneeName}` : null}
                            {c.outcome && (
                              <span className="text-primary"> · завершено · {OUTCOME_LABEL[c.outcome as TaskOutcome] ?? c.outcome}</span>
                            )}
                          </div>
                        </div>
                        <button type="button" onClick={() => setSelectedTaskId(c.id)} className="text-xs text-muted-foreground hover:text-foreground">
                          Деталі →
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="overflow-hidden rounded-xl border bg-card">
                <header className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold">
                  Мої запити у відділи
                  <span className="rounded bg-violet px-1.5 py-0.5 text-[10px] font-bold text-violet-foreground">
                    3B · InternalRequest
                  </span>
                </header>
                {myReqQ.isPending ? (
                  <ListSkeleton inset />
                ) : myReqQ.isError ? (
                  <ListError message={(myReqQ.error as Error)?.message} onRetry={() => void myReqQ.refetch()} inset />
                ) : (myReqQ.data?.items.length ?? 0) === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                    Запитів немає. «+ Створити → Запит у відділ» — піде в чергу відділу, не людині.
                  </p>
                ) : (
                  <ul>
                    {myReqQ.data!.items.map((r) => (
                      <li key={r.id} className="flex items-center gap-3 border-b px-4 py-2.5 text-sm last:border-b-0">
                        <Send className="h-4 w-4 shrink-0 text-violet-foreground" aria-hidden />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">
                            {r.departmentName ? `${r.departmentName}: ` : ""}{r.subject}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            Взяв: {r.assigneeName ?? "—"} · {REQUEST_STATUS_LABEL[r.status] ?? r.status}
                            {r.overdue && <span className="text-destructive"> · прострочено</span>}
                            {r.result ? ` · ${r.result}` : ""}
                          </p>
                        </div>
                        <Link to="/requests" className="shrink-0 text-xs text-primary hover:underline">
                          До запитів →
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <p className="px-1 text-[11px] text-muted-foreground">
                §6.2: запит у відділ — окрема сутність (3B), НЕ задача. Дві секції, не злиті.
              </p>
            </div>
          )}

          {/* ════ TAB 3: Follow-up (§6.3) ════ */}
          {tab === "fu" && (
            fuQ.isPending ? (
              <ListSkeleton />
            ) : fuQ.isError ? (
              <ListError message={(fuQ.error as Error)?.message} onRetry={() => void fuQ.refetch()} />
            ) : (fuQ.data?.items.length ?? 0) === 0 ? (
              <div className="rounded-lg border bg-muted/30 p-8 text-center">
                <CornerDownRight className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
                <p className="text-sm font-medium">Follow-up задач немає</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Follow-up створюється з результату задачі/дзвінка або вручну («+ Створити → Задача», тип Follow-up).
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {fuQ.data!.items.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onOpen={() => setSelectedTaskId(task.id)}
                    onComplete={() => openComplete(task)}
                    isOwn={canEditAnyTask || (!!currentUserId && task.assigneeId === currentUserId)}
                  />
                ))}
              </div>
            )
          )}

          {/* ════ TAB 4: По клієнтах (§6.4, holding-grouped) ════ */}
          {tab === "byclient" && (
            byClientQ.isPending ? (
              <ListSkeleton />
            ) : byClientQ.isError ? (
              <ListError message={(byClientQ.error as Error)?.message} onRetry={() => void byClientQ.refetch()} />
            ) : (byClientQ.data?.groups.length ?? 0) === 0 ? (
              <div className="rounded-lg border bg-muted/30 p-8 text-center">
                <Building2 className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
                <p className="text-sm font-medium">Немає відкритих клієнтських задач</p>
                <p className="mt-1 text-xs text-muted-foreground">Задачі без клієнта — у вкладці «Мені треба зробити».</p>
              </div>
            ) : (
              <div className="space-y-3">
                {byClientQ.data!.groups.map((g) => {
                  const expanded = expandedHoldings[g.holdingId] ?? true;
                  return (
                    <section key={g.holdingId} className="overflow-hidden rounded-xl border bg-card">
                      <header className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-2.5">
                        <button
                          type="button"
                          onClick={() => setExpandedHoldings((p) => ({ ...p, [g.holdingId]: !expanded }))}
                          className="flex min-w-0 items-center gap-1.5 text-sm font-semibold"
                        >
                          {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                          <span className="truncate">{g.holdingName ?? "Клієнт"}</span>
                        </button>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="rounded bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                            {g.counts.total} {g.counts.total === 1 ? "задача" : "задач"}
                            {g.counts.overdue > 0 && <span className="text-destructive"> · {g.counts.overdue} простроч.</span>}
                          </span>
                          {/* D-142.W1 (П-3) — restricted-група watched-задач: без Client360-лінка */}
                          {!g.restricted && (
                            <Link
                              to={clientRoutes.client(g.holdingId)}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Client360 →
                            </Link>
                          )}
                        </span>
                      </header>
                      {expanded && (
                        <div className="space-y-1.5 p-3">
                          {g.items.map((task) => (
                            <TaskRow
                              key={task.id}
                              task={task}
                              showAssignee={scope !== "my"}
                              onOpen={() => setSelectedTaskId(task.id)}
                              onComplete={() => openComplete(task)}
                              isOwn={canEditAnyTask || (!!currentUserId && task.assigneeId === currentUserId)}
                            />
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )
          )}

          {/* ════ TAB 5: Команда — supervisor team-task-queue ONLY (§6.5/R4) ════ */}
          {tab === "team" && teamAllowed && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {([
                  { k: "all", label: "Усі" },
                  { k: "overdue", label: "Прострочені" },
                  { k: "no_outcome", label: "Завершені без результату" },
                ] as Array<{ k: TeamChip; label: string }>).map((c) => (
                  <button
                    key={c.k}
                    type="button"
                    onClick={() => setTeamChip(c.k)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      teamChip === c.k
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {c.label}
                  </button>
                ))}
                {teamManagers.length > 1 && (
                  <select
                    value={byManager}
                    onChange={(e) => setByManager(e.target.value)}
                    className="rounded-full border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground"
                  >
                    <option value="">Усі менеджери</option>
                    {teamManagers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {teamQ.isPending ? (
                <ListSkeleton />
              ) : teamQ.isError ? (
                <ListError message={(teamQ.error as Error)?.message} onRetry={() => void teamQ.refetch()} />
              ) : (teamQ.data?.items.length ?? 0) === 0 ? (
                <div className="rounded-lg border bg-muted/30 p-8 text-center">
                  <p className="text-sm font-medium">У черзі команди порожньо</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {teamChip === "no_outcome"
                      ? "Завершених без результату немає — outcome-gate тримає якість даних."
                      : "Задачі команди з'являться тут."}
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {teamQ.data!.items.map((task) => (
                    <div key={task.id} className="relative">
                      <TaskRow
                        task={task}
                        showAssignee
                        done={teamChip === "no_outcome"}
                        onOpen={() => setSelectedTaskId(task.id)}
                        onComplete={teamChip === "no_outcome" ? undefined : () => openComplete(task)}
                        isOwn={canEditAnyTask || (!!currentUserId && task.assigneeId === currentUserId)}
                      />
                      {task.legacy && (
                        <span className="absolute right-3 top-2 inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-bold text-destructive">
                          <AlertTriangle className="h-3 w-3" aria-hidden /> LEGACY · без outcome (до R1)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="px-1 text-[11px] text-muted-foreground">
                §6.5: team-task-queue (resolver scope=team). Повний Supervisor Control — окремий модуль (TIER 4).
              </p>
            </>
          )}
        </div>
      </div>

      <TaskDetailDrawer
        open={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        taskId={selectedTaskId}
      />

      {/* canonical outcome-required completion (D-031; row-CTA «Завершити») */}
      <TaskCompleteModal
        open={!!completeTarget}
        onClose={() => setCompleteTarget(null)}
        task={completeTarget}
      />
    </>
  );
}

// ── small shared pieces ──

function ListSkeleton({ inset }: { inset?: boolean }) {
  return (
    <div className={cn("space-y-2", inset && "p-3")}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-16" />
      ))}
    </div>
  );
}

function ListError({ message, onRetry, inset }: { message?: string; onRetry: () => void; inset?: boolean }) {
  return (
    <div className={cn("rounded-lg border bg-destructive/5 p-6 text-center", inset && "m-3")}>
      <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
      <p className="text-sm text-destructive">{message ?? "Не вдалося завантажити"}</p>
      <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={onRetry}>
        <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
      </Button>
    </div>
  );
}

function EmptyState({ createSlot }: { createSlot?: ReactNode }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-8 text-center">
      <p className="text-sm font-medium">Немає задач у цьому фільтрі</p>
      <p className="mt-1 text-xs text-muted-foreground">Створіть нову задачу або змініть фільтр.</p>
      {createSlot && <div className="mt-3 flex justify-center">{createSlot}</div>}
    </div>
  );
}

function DoneDot() {
  return (
    <div className="grid h-4.5 w-4.5 shrink-0 place-items-center rounded-full bg-primary">
      <svg viewBox="0 0 16 16" className="h-2.5 w-2.5 text-primary-foreground" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 8.5l3 3 7-7" />
      </svg>
    </div>
  );
}
